<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { HourlyPeriod } from "@types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            timeline: {
                hc: "Heures creuses (HC)",
                hp: "Heures pleines (HP)",
                heuresPointesFixes: "Pointe fixe (P)",
                hours: "({hoursNb} heures)",
                tooltip: {
                    pte: "Les heures de pointes fixes sont uniquement activées entre décembre à février.",
                },
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface TimelineSegment {
    start: number
    end: number
    raw?: HourlyPeriod
    durationHours?: number
}

interface ViewSegment {
    kind: "HP" | "HC" | "PT" | "E"
    start: number
    end: number
    startLabel?: string
    endLabel?: string
    style: Record<string, string>
    raw?: HourlyPeriod
    index: number
    nbHours?: number
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /**
         * Heures creuses
         */
        heuresCreuses?: HourlyPeriod[] | null
        /**
         * Heures de pointes fixes
         */
        heuresPointesFixes?: HourlyPeriod[] | null
        /**
         * Facteur d'échelle pour la timeline
         */
        scale?: number
    }>(),
    {
        scale: 1,
        heuresCreuses: () => [],
        heuresPointesFixes: () => [],
    }
)

// ================================
// COMPUTED PROPERTIES
// ================================
/**
 * Segments normalisés des heures creuses
 */
const getHcSegments = computed(() => normalize(props.heuresCreuses || []))

/**
 * Segments normalisés des heures de pointe fixe
 */
const getPointesSegments = computed(() =>
    normalize(props.heuresPointesFixes || [])
)

/**
 * Segments des heures pleines (complément des HC et PT)
 */
const getHpSegments = computed(() => {
    const union = normalize([
        ...(props.heuresCreuses || []),
        ...(props.heuresPointesFixes || []),
    ])
    const res: Array<{ start: number; end: number }> = []
    let cursor = 0
    union.forEach((seg) => {
        if (cursor < seg.start) res.push({ start: cursor, end: seg.start })
        cursor = Math.max(cursor, seg.end)
    })
    if (cursor < 1440) res.push({ start: cursor, end: 1440 })
    return res
})

/**
 * Vue unifiée de tous les segments pour l'affichage
 */
const getView = computed<ViewSegment[]>(() => {
    const hc = viewFrom("HC", getHcSegments.value)
    const pt = viewFrom("PT", getPointesSegments.value)
    const hp = viewFrom("HP", getHpSegments.value)

    if (!hc.length && !pt.length) {
        return [
            {
                kind: "E",
                start: 0,
                end: 1440,
                startLabel: "00h00",
                endLabel: "24h00",
                style: blockStyle(0, 1440),
                index: 0,
                nbHours: 24,
            },
        ]
    }

    return [...hc, ...pt, ...hp].sort((a, b) => a.start - b.start)
})

// ================================
// METHODS
// ================================
/**
 * Convertit une heure au format HH:MM:SS en minutes depuis minuit
 * @param hhmmss - Heure au format HH:MM:SS
 * @returns Nombre de minutes depuis minuit
 */
function toMin(hhmmss: string): number {
    const [h, m, s] = hhmmss.split(":").map(Number)
    return (h % 24) * 60 + (m || 0) + Math.floor((s || 0) / 60)
}

/**
 * Normalise et fusionne les périodes horaires en segments continus
 * @param periods - Tableau des périodes horaires
 * @returns  Segments normalisés avec durée en heures
 */
function normalize(periods: HourlyPeriod[]): TimelineSegment[] {
    const result: Array<{
        start: number
        end: number
        raw: HourlyPeriod
    }> = []

    periods.forEach((p) => {
        const startMinute = toMin(p.time_from)
        const endMinute = toMin(p.time_to)

        if (startMinute === endMinute) return

        // Vérifier si la plage traverse minuit
        if (startMinute > endMinute) {
            // Diviser en deux segments : de startMinute à 1440 et de 0 à endMinute
            result.push({
                start: startMinute,
                end: 1440,
                raw: p,
            })
            result.push({
                start: 0,
                end: endMinute,
                raw: p,
            })
        } else {
            result.push({
                start: startMinute,
                end: endMinute,
                raw: p,
            })
        }
    })

    result.sort((a, b) => a.start - b.start)

    const merged: Array<{
        start: number
        end: number
        raw: HourlyPeriod
    }> = []
    for (const seg of result) {
        const last = merged[merged.length - 1]
        if (last && seg.start <= last.end) {
            last.end = Math.max(last.end, seg.end)
        } else {
            merged.push({ ...seg })
        }
    }

    return merged.map((s) => {
        return {
            start: s.start,
            end: s.end,
            raw: s.raw,
            durationHours: (s.end - s.start) / 60,
        }
    })
}

/**
 * Calcule les styles CSS pour positionner un segment sur la timeline
 * @param start - Position de début en minutes
 * @param end - Position de fin en minutes
 * @returns Styles CSS avec variables --left et --width
 */
function blockStyle(start: number, end: number): Record<string, string> {
    // Pour les segments normaux (qui ne traversent pas minuit)
    const width = end - start
    return {
        "--left": `calc(${start} * 100% / 1440)`,
        "--width": `calc(${width} * 100% / (1440 - 1))`,
    }
}

/**
 * Formate une position en minutes en format HHhMM
 * @param min - Position en minutes depuis minuit
 * @returns Heure formatée (ex: 12h30)
 */
function formatFromMin(min: number): string {
    const h = Math.floor(min / 60) % 24
    const m = min % 60
    return `${String(h).padStart(2, "0")}h${String(m).padStart(2, "0")}`
}

/**
 * Transforme les segments techniques en segments d'affichage
 * @param kind - Type de segment
 * @param segs - Segments techniques
 * @returns Segments formatés pour l'affichage
 */
function viewFrom(
    kind: "HP" | "HC" | "PT",
    segs: TimelineSegment[]
): ViewSegment[] {
    return segs.map((s, i) => {
        // Toujours utiliser les heures calculées à partir des positions en minutes
        // pour afficher les heures réelles de chaque segment
        const startLabel = formatFromMin(s.start)
        const endLabel = formatFromMin(s.end)

        return {
            kind,
            start: s.start,
            end: s.end,
            startLabel,
            endLabel,
            style: blockStyle(s.start, s.end),
            raw: s.raw,
            index: i,
            nbHours: s.durationHours ?? (s.end - s.start) / 60,
        }
    })
}

/**
 * Formate le nombre d'heures en format lisible (ex: "2 heures" ou "1h 30min")
 * @param hours - Nombre d'heures décimal
 * @returns Durée formatée
 */
function formatHours(hours: number): string {
    const h = Math.floor(hours)
    const m = Math.round((hours - h) * 60)

    if (m === 60) {
        return `${h + 1} heure${h + 1 > 1 ? "s" : ""}`
    }

    if (m === 0) return `${h} heure${h > 1 ? "s" : ""}`

    return `${h}h ${m}min${m > 1 ? "s" : ""}`
}

function getTooltipContent(segment: ViewSegment): string {
    return `${segment.startLabel} - ${segment.endLabel}\n${formatHours(segment.nbHours ?? 0)}`
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <div class="meter-timeline">
        <!-- ================================ -->
        <!-- HEADER/EN-TÊTE - LÉGENDES -->
        <!-- ================================ -->
        <div class="meter-timeline__header">
            <div class="meter-timeline__legend">
                <span class="legend legend--hc" />
                <small>{{ t("timeline.hc") }}</small>
            </div>
            <div class="meter-timeline__legend">
                <span class="legend legend--hp" />
                <small>{{ t("timeline.hp") }}</small>
            </div>
            <div
                class="meter-timeline__legend"
                v-if="heuresPointesFixes?.length"
            >
                <span class="legend legend--pt" />
                <small
                    >{{ t("timeline.heuresPointesFixes") }}
                    <i
                        v-tooltip="t('timeline.tooltip.pte')"
                        class="pi pi-info-circle"
                        style="font-size: 0.8rem"
                    ></i
                ></small>
            </div>
        </div>

        <!-- ================================ -->
        <!-- CONTENU PRINCIPAL - TIMELINE -->
        <!-- ================================ -->
        <div class="meter-timeline__bar">
            <div
                v-for="(v, k) in getView"
                :key="k"
                class="meter-timeline__seg"
                :data-kind="v.kind"
                :style="v.style"
                :class="[
                    v.start === 0 && 'meter-timeline__radius-left',
                    v.end === 1440 && 'meter-timeline__radius-right',
                ]"
                v-tooltip.bottom="getTooltipContent(v)"
            >
                <div
                    v-if="
                        (v.kind === 'HC' ||
                            v.kind === 'HP' ||
                            v.kind === 'PT') &&
                        v.startLabel &&
                        v.endLabel
                    "
                    class="meter-timeline__label"
                    :data-kind="v.kind"
                >
                    <div class="meter-timeline__dates">
                        {{ `${v.startLabel} - ${v.endLabel}` }}
                    </div>
                    <div class="meter-timeline__hours">
                        ({{ formatHours(v.nbHours ?? 0) }})
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* ================================ */
/* COMPOSANT PRINCIPAL */
/* ================================ */
.meter-timeline {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;

    --h: 1.25rem;
    --label-offset: 1.25rem;
    --hour-width: 3.25rem;
    --offset-top-hour: 1.5rem;
}

/* ================================ */
/* HEADER/EN-TÊTE - LÉGENDES */
/* ================================ */
.meter-timeline__header {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
}

.meter-timeline__legend {
    display: flex;
    gap: 0.5rem;
    align-items: center;
}

.meter-timeline .legend {
    width: 0.75rem;
    height: 0.75rem;
    display: inline-block;
    border-radius: 50%;
}

.meter-timeline .legend--hp {
    background-color: var(--p-indigo-400);
}

.meter-timeline .legend--hc {
    background-color: var(--p-cyan-500);
}

.meter-timeline .legend--pt {
    background-color: var(--pt-color, #fb923c);
}

.meter-timeline .legend--e {
    background-color: #cfe0ff;
}

/* ================================ */
/* CONTENU - TIMELINE HORIZONTALE */
/* ================================ */
.meter-timeline__bar {
    position: relative;
    height: var(--h);
    width: 100%;
    background: var(--hp-bg, #cfe0ff);
    border-radius: 0.5rem;
    overflow: visible;
    margin-bottom: var(--label-offset);
}

.meter-timeline__bar--empty {
    background: var(--p-bg, #cfe0ff);
}

.meter-timeline__seg {
    position: absolute;
    height: var(--h);
    width: var(--width);
    left: var(--left);
    opacity: 0.7;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 1;
    }
}

.meter-timeline__seg[data-kind="HC"] {
    background: var(--hc-bg, var(--p-cyan-500));
}

.meter-timeline__seg[data-kind="HP"] {
    background: var(--p-indigo-400);
}

.meter-timeline__seg[data-kind="PT"] {
    background: var(--pt-bg, var(--pt-color, #fb923c));
}

/* ================================ */
/* LABELS DES SEGMENTS */
/* ================================ */
.meter-timeline__label {
    position: relative;
    bottom: -1.5rem;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
    font-size: 12px;
    font-weight: 700;
    line-height: 1rem;
    padding: 0 0.25rem;
    z-index: 5;
}

.meter-timeline__dates {
    white-space: nowrap;
    font-weight: 700;
    line-height: 1rem;
    overflow: hidden;
    text-overflow: ellipsis;
}

.meter-timeline__hours {
    white-space: nowrap;
    font-weight: 500;
    line-height: 1rem;
    overflow: hidden;
    text-overflow: ellipsis;
}

.meter-timeline__label[data-kind="HP"] {
    color: var(--p-indigo-400);
}

.meter-timeline__label[data-kind="HC"] {
    color: var(--p-cyan-500);
}

.meter-timeline__label[data-kind="PT"] {
    color: var(--pt-color, #fb923c);
}

/* ================================ */
/* BORDS ARRONDIES */
/* ================================ */
.meter-timeline__radius-left {
    border-top-left-radius: 0.5rem;
    border-bottom-left-radius: 0.5rem;
}

.meter-timeline__radius-right {
    border-top-right-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
}

/* ================================ */
/* RESPONSIVE/BREAKPOINTS - MODE VERTICAL */
/* ================================ */
@media (max-width: 1024px) {
    .meter-timeline {
        --label-offset: 0.75rem;
    }

    .meter-timeline__bar {
        width: var(--h);
        height: 320px;
        min-height: 240px;
        margin-bottom: 0;
    }

    .meter-timeline__seg {
        position: absolute;
        left: 0;
        top: var(--left);
        width: var(--h);
        height: var(--width);
    }

    .meter-timeline__label {
        top: 0;
        bottom: auto;
        left: calc(100% + 8px);
        transform: none;
        text-align: left;
        display: flex;
        flex-direction: row;
        gap: 0.5rem;
        align-items: center;
    }

    .meter-timeline__dates {
        overflow: visible;
    }

    .meter-timeline__hours {
        overflow: visible;
    }

    .meter-timeline__radius-left {
        border-top-left-radius: 0.5rem;
        border-top-right-radius: 0.5rem;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }

    .meter-timeline__radius-right {
        border-bottom-left-radius: 0.5rem;
        border-bottom-right-radius: 0.5rem;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
    }
}
</style>
